import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Home, Sparkles, Crown, MessageCircle, Stethoscope, Volume2, VolumeX } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { vibrate } from "@/lib/api";
import { isSoundOn, toggleSound } from "@/lib/feedback";
import { APP_NAME } from "@/config";

const NAV = [
  { to: "/app", icon: Home, label: "Accueil", id: "nav-home" },
  { to: "/import", icon: Sparkles, label: "Analyser", id: "nav-import" },
  { to: "/doctor", icon: Stethoscope, label: "Docteur", id: "nav-doctor" },
  { to: "/whatsapp", icon: MessageCircle, label: "WhatsApp", id: "nav-whatsapp" },
  { to: "/pricing", icon: Crown, label: "Premium", id: "nav-pricing" },
];

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sound, setSound] = React.useState(isSoundOn());

  return (
    <div className="grain min-h-screen bg-[#09090E] text-slate-100">
      {/* Ambient framing glow */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full bg-rose-600/18 blur-[130px]" />
        <div className="absolute top-1/3 -right-40 w-96 h-96 rounded-full bg-purple-600/18 blur-[130px]" />
      </div>

      <div className="mx-auto max-w-md md:max-w-lg min-h-screen flex flex-col relative">
        <header className="glass sticky top-0 z-30 flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
          <button
            data-testid="header-logo"
            onClick={() => navigate("/app")}
            className="flex items-center gap-2.5 group"
          >
            <img src="/icons/icon-512.png" alt={APP_NAME} className="w-8 h-8 rounded-xl ring-1 ring-white/10 transition-transform group-active:scale-95" />
            <span className="font-display text-lg font-extrabold tracking-tight">{APP_NAME}</span>
          </button>
          {user && (
            <div className="flex items-center gap-3">
              <button
                data-testid="sound-toggle"
                onClick={() => setSound(toggleSound())}
                aria-label={sound ? "Couper le son" : "Activer le son"}
                className="text-slate-500 hover:text-rose-400 transition-colors"
              >
                {sound ? <Volume2 size={17} /> : <VolumeX size={17} />}
              </button>
              <button
                data-testid="logout-button"
                onClick={() => { vibrate(); logout(); navigate("/"); }}
                className="text-xs font-medium text-slate-400 hover:text-rose-400 transition-colors"
              >
                Déconnexion
              </button>
            </div>
          )}
        </header>

        <main className="flex-1 px-5 py-6 pb-32">{children}</main>

        <nav className="fixed bottom-4 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-sm z-40">
          <div className="glass rounded-full border border-white/[0.12] shadow-[0_8px_32px_rgba(0,0,0,0.5)] grid grid-cols-5 px-1.5 py-1.5">
            {NAV.map(({ to, icon: Icon, label, id }) => {
              const active = location.pathname === to;
              return (
                <button
                  key={to}
                  data-testid={id}
                  onClick={() => { vibrate(); navigate(to); }}
                  className={`relative flex flex-col items-center gap-1 py-2 rounded-full transition-colors ${
                    active ? "text-white" : "text-slate-500 hover:text-slate-300"
                  }`}
                >
                  <Icon size={20} strokeWidth={active ? 2.5 : 2} className={active ? "text-rose-400" : ""} />
                  <span className="text-[9.5px] font-semibold tracking-wide">{label}</span>
                  {active && <span className="absolute -bottom-0.5 h-1 w-1 rounded-full bg-gradient-to-r from-rose-500 to-purple-500" />}
                </button>
              );
            })}
          </div>
        </nav>
      </div>
    </div>
  );
}
